import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Cropper from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import 'react-easy-crop/react-easy-crop.css';
import './index.scss';

const DEFAULT_CROP = Object.freeze({ x: 0, y: 0 });
const DEFAULT_ZOOM_RANGE = Object.freeze({ min: 1, max: 3, step: 0.01 });
const KEYBOARD_PAN_STEP = 8;
const KEYBOARD_PAN_LARGE_STEP = 24;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const restrictCropPosition = (position, mediaSize, cropSize, zoom) => {
    if (!mediaSize || !cropSize) {
        return position;
    }

    const maxX = Math.max((mediaSize.width * zoom) / 2 - cropSize.width / 2, 0);
    const maxY = Math.max((mediaSize.height * zoom) / 2 - cropSize.height / 2, 0);

    return {
        x: clamp(position.x, -maxX, maxX),
        y: clamp(position.y, -maxY, maxY),
    };
};

const CircularImageCropper = ({
    image,
    disabled,
    interactive,
    onCropComplete,
    onInteractionChange,
    resetSignal,
    zoomRange,
    instructions,
    size,
    width,
    height,
    aspect,
    cropShape,
    className,
}) => {
    const { t } = useTranslation();
    const baseRange = useMemo(() => ({ ...DEFAULT_ZOOM_RANGE, ...zoomRange }), [zoomRange]);
    const baseStep = baseRange.step || 0.01;
    const [crop, setCrop] = useState(DEFAULT_CROP);
    const [zoom, setZoom] = useState(baseRange.min);
    const [minZoom, setMinZoom] = useState(baseRange.min);
    const [maxZoom, setMaxZoom] = useState(Math.max(baseRange.max, baseRange.min));
    const [hasInteracted, setHasInteracted] = useState(false);
    const [mediaSize, setMediaSize] = useState(null);
    const [cropSize, setCropSize] = useState(null);
    const mediaInitializedRef = useRef(false);
    const [sliderValue, setSliderValue] = useState(0);

    const notifyInteraction = useCallback((value) => {
        setHasInteracted(value);
        onInteractionChange?.(value);
    }, [onInteractionChange]);

    const updateZoomWithSlider = useCallback((nextZoom, bounds = {}) => {
        const currentMin = bounds.min ?? minZoom;
        const currentMax = bounds.max ?? maxZoom;
        const clamped = Math.min(Math.max(nextZoom, currentMin), currentMax);
        setZoom(clamped);
        const range = currentMax - currentMin;
        const normalized = range > 0 ? (clamped - currentMin) / range : 0;
        setSliderValue(Number.isFinite(normalized) ? normalized : 0);
        return clamped;
    }, [minZoom, maxZoom]);

    const resetState = useCallback((overrideMin, bounds) => {
        const targetMin = overrideMin ?? minZoom;
        setCrop(DEFAULT_CROP);
        updateZoomWithSlider(targetMin, bounds);
        notifyInteraction(false);
        onCropComplete?.(null);
    }, [minZoom, notifyInteraction, onCropComplete, updateZoomWithSlider]);

    useEffect(() => {
        resetState();
        mediaInitializedRef.current = false;
    }, [image, resetSignal, resetState]);

    useEffect(() => {
        if (!interactive) {
            setMinZoom(baseRange.min);
            const nextMax = Math.max(baseRange.max, baseRange.min);
            setMaxZoom(nextMax);
            resetState(baseRange.min, { min: baseRange.min, max: nextMax });
        }
        mediaInitializedRef.current = false;
    }, [interactive, baseRange.min, baseRange.max, resetState]);

    const handleCropChange = useCallback((nextCrop) => {
        setCrop(nextCrop);
        if (!interactive) {
            return;
        }

        if (!hasInteracted && (Math.abs(nextCrop.x) > 0.01 || Math.abs(nextCrop.y) > 0.01)) {
            notifyInteraction(true);
        }
    }, [interactive, hasInteracted, notifyInteraction]);

    const handleZoomChange = useCallback((value) => {
        const clampedValue = updateZoomWithSlider(value);
        if (!interactive) {
            return;
        }
        if (!hasInteracted && Math.abs(clampedValue - minZoom) > 0.01) {
            notifyInteraction(true);
        }
    }, [interactive, hasInteracted, minZoom, notifyInteraction, updateZoomWithSlider]);

    const handleSliderChange = useCallback((value) => {
        const numeric = Number(value);
        const clampedRatio = Math.min(Math.max(numeric, 0), 1);
        const range = maxZoom - minZoom;
        const targetZoom = minZoom + range * clampedRatio;
        const appliedZoom = updateZoomWithSlider(targetZoom);
        if (interactive && Math.abs(appliedZoom - minZoom) > 0.01) {
            notifyInteraction(true);
        }
    }, [interactive, minZoom, maxZoom, notifyInteraction, updateZoomWithSlider]);

    const handleCropComplete = useCallback((_, areaPixels) => {
        onCropComplete?.(areaPixels);
    }, [onCropComplete]);

    const handleViewportKeyDown = useCallback((event) => {
        if (!interactive || disabled) {
            return;
        }

        const step = event.shiftKey ? KEYBOARD_PAN_LARGE_STEP : KEYBOARD_PAN_STEP;
        const panDeltaByKey = {
            ArrowUp: { x: 0, y: -step },
            ArrowDown: { x: 0, y: step },
            ArrowLeft: { x: -step, y: 0 },
            ArrowRight: { x: step, y: 0 },
        };
        const delta = panDeltaByKey[event.key];

        if (!delta) {
            return;
        }

        event.preventDefault();
        setCrop((currentCrop) => restrictCropPosition({
            x: currentCrop.x + delta.x,
            y: currentCrop.y + delta.y,
        }, mediaSize, cropSize, zoom));

        if (!hasInteracted) {
            notifyInteraction(true);
        }
    }, [cropSize, disabled, hasInteracted, interactive, mediaSize, notifyInteraction, zoom]);

    const handleMediaLoaded = useCallback((loadedMediaSize) => {
        if (!interactive) {
            return;
        }
        setMediaSize(loadedMediaSize || null);

        const viewportWidth = width || size;
        const viewportHeight = height || size;
        const naturalWidth = loadedMediaSize?.naturalWidth || viewportWidth;
        const naturalHeight = loadedMediaSize?.naturalHeight || viewportHeight;

        if (!naturalWidth || !naturalHeight) {
            const fallbackMin = Math.max(baseRange.min, 1);
            const fallbackMax = Math.max(baseRange.max, fallbackMin + 0.5);
            setMinZoom(fallbackMin);
            setMaxZoom(fallbackMax);
            resetState(fallbackMin, { min: fallbackMin, max: fallbackMax });
            return;
        }

        const coverScale = Math.max(viewportWidth / naturalWidth, viewportHeight / naturalHeight);
        const minMultiplier = Math.max(baseRange.min, 0.1);
        const maxMultiplier = Math.max(baseRange.max, minMultiplier + 0.5);

        const nextMinZoom = Math.max(coverScale * minMultiplier, 0.1);
        const nextMaxZoom = Math.max(nextMinZoom + 0.5, coverScale * maxMultiplier);

        if (
            mediaInitializedRef.current &&
            Math.abs(nextMinZoom - minZoom) < 0.0001 &&
            Math.abs(nextMaxZoom - maxZoom) < 0.0001
        ) {
            return;
        }

        setMinZoom(nextMinZoom);
        setMaxZoom(nextMaxZoom);
        resetState(nextMinZoom, { min: nextMinZoom, max: nextMaxZoom });
        mediaInitializedRef.current = true;
    }, [interactive, size, width, height, baseRange.min, baseRange.max, minZoom, maxZoom, resetState]);

    const handleCropSizeChange = useCallback((nextCropSize) => {
        setCropSize(nextCropSize || null);
    }, []);

    const frameWidth = width || size;
    const frameHeight = height || size;
    const containerClassName = [
        'circular-image-cropper',
        cropShape === 'rect' ? 'circular-image-cropper--rect' : '',
        className
    ].filter(Boolean).join(' ');
    const showInteractiveCropper = interactive && Boolean(image);
    const sliderStep = useMemo(() => (
        Number.isFinite(baseStep) && baseStep > 0 ? baseStep : 0.01
    ), [baseStep]);

    return (
        <div className={containerClassName}>
            <div
                className="circular-image-cropper__frame"
                style={{ width: frameWidth, height: frameHeight }}
            >
                {showInteractiveCropper ? (
                    <div
                        className="circular-image-cropper__viewport"
                        role="group"
                        aria-label={t('imageCropper.panAria', 'Reposition image')}
                        aria-disabled={disabled ? 'true' : undefined}
                        tabIndex={disabled ? -1 : 0}
                        onKeyDown={handleViewportKeyDown}
                    >
                        <Cropper
                            image={image}
                            crop={crop}
                            zoom={zoom}
                            aspect={aspect}
                            cropShape={cropShape}
                            showGrid={false}
                            minZoom={minZoom}
                            maxZoom={maxZoom}
                            zoomWithScroll
                            onCropChange={handleCropChange}
                            onZoomChange={handleZoomChange}
                            onCropComplete={handleCropComplete}
                            onCropAreaChange={handleCropComplete}
                            onMediaLoaded={handleMediaLoaded}
                            onCropSizeChange={handleCropSizeChange}
                            objectFit="cover"
                            disabled={disabled}
                        />
                    </div>
                ) : image ? (
                    <div className="circular-image-cropper__static">
                        <img src={image} alt={t('imageCropper.selectedAlt', 'Selected')} />
                    </div>
                ) : (
                    <div className="circular-image-cropper__empty">{t('imageCropper.empty', 'No image selected')}</div>
                )}
            </div>
            {showInteractiveCropper && (
                <div className="circular-image-cropper__controls">
                    <div className="circular-image-cropper__label">
                        {instructions || t('imageCropper.instructions', 'Adjust zoom and drag image to reposition')}
                    </div>
                    <input
                        type="range"
                        min={0}
                        max={1}
                        step={sliderStep}
                        value={sliderValue}
                        onChange={(event) => handleSliderChange(event.target.value)}
                        disabled={disabled}
                        aria-label={t('imageCropper.zoomAria', 'Adjust image zoom')}
                    />
                </div>
            )}
        </div>
    );
};

CircularImageCropper.propTypes = {
    image: PropTypes.string,
    disabled: PropTypes.bool,
    interactive: PropTypes.bool,
    onCropComplete: PropTypes.func,
    onInteractionChange: PropTypes.func,
    resetSignal: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    zoomRange: PropTypes.shape({
        min: PropTypes.number,
        max: PropTypes.number,
        step: PropTypes.number,
    }),
    instructions: PropTypes.string,
    size: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    aspect: PropTypes.number,
    cropShape: PropTypes.oneOf(['round', 'rect']),
    className: PropTypes.string,
};

CircularImageCropper.defaultProps = {
    image: null,
    disabled: false,
    interactive: false,
    onCropComplete: undefined,
    onInteractionChange: undefined,
    resetSignal: 0,
    zoomRange: DEFAULT_ZOOM_RANGE,
    instructions: null,
    size: 200,
    width: undefined,
    height: undefined,
    aspect: 1,
    cropShape: 'round',
    className: undefined,
};

export default CircularImageCropper;
